import * as React from 'react';
import { useParams } from 'react-router-dom';
import ExpandLeft from '../../assets/icons/ExpandLeft';
import EditIcon from '../../assets/icons/EditIcon';
import { GroupButton, SidebarDrawer } from '../../components/common';
import DeleteIcon from '../../assets/icons/DeleteIcon';
import { AddSquareIcon, NariaIcon, WidgetIcon } from '../../assets/icons';
import PurchaseTable from '../../components/purchase/Table';
import { InfoCard, InfoCardProps } from '../../components/core/InfoCard';
import { VariationTable } from '../../components/variation/Table';
import AddSquare from '../../assets/icons/AddSquare';
import { getInventory } from '../../backend-services';
import PageLoader from '../../components/common/PageLoader';
import { toast } from 'react-hot-toast';
import { RestockHistory, Variation } from '../../@types/inventory';
import BinIcon from '../../assets/icons/BinIcon';
import TruncatedText from '../../components/TruncatedText';
import { InventoryModal } from '../../components/inventory/InventoryModal';
import { createPortal } from 'react-dom';
import Restock from '../../components/inventory/restock/Restock';
import EditInventory from '../../components/inventory/edit/EditInventory';
import VariantDeletePrompt from '../../components/inventory/view/VariantDeletePrompt';
import InventoryDeletePrompt from '../../components/inventory/InventoryDeletePrompt';
import { formattedDate } from '../../utils/formatDate';
import Naira from '../../assets/icons/Naira';
import { formatMoney } from '../../utils';
import { stat } from 'fs/promises';

const ViewInventoryPage: React.FC = () => {
  const { inventoryId } = useParams();

  const [isLoading, setIsLoading] = React.useState(false);
  const [variations, setVariations] = React.useState([]);
  const [details, setDetails] = React.useState<
    { unitprice: number; quantity: number; desc: string; name: string; date: string } | undefined
  >();
  const [openModal, setOpenModal] = React.useState(false);
  const [showPrompt, setShowPrompt] = React.useState(false);
  const [showDeleteVariant, setShowDeleteVariant] = React.useState(false);
  const [variationId, setVariationId] = React.useState('');
  const [showDeleteInventory, setShowDeleteInventory] = React.useState(false);
  const [restock, setRestock] = React.useState([]);
  const [stats, setStats] = React.useState({
    totalCostPrice: 0,
    totalSellingPrice:0,
    totalPurchase: 0,
  });

  const handleModal = () => setOpenModal(true);

  const onClose = () => setShowPrompt(false);

  React.useEffect(() => {
    if (!inventoryId) throw new Error('Inventory Id is missing');
    getInventoryDetail(inventoryId);
  }, [inventoryId]);

  const getInventoryDetail = async (id: string) => {
    setIsLoading(true);
    try {
      const res = await getInventory(id);
      console.log(res);
      setVariations(res.variations);
      setRestock(res.restockHistory);
      const inventoryDetails = {
        unitprice: res.rate,
        quantity: res.quantity,
        desc: res.description,
        name: res.name,
        date: res.createdAt,
      };
      setStats({
        ...stats,
        totalCostPrice: res.totalCostPrice,
        totalSellingPrice: res.totalSellingPrice,
      });
      setDetails(inventoryDetails);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      toast.error('Failed to fetch variations');
    }
  };

  const handleDeleteVariant = (variationId: string | undefined) => {
    console.log(variationId);
    setShowDeleteVariant(true);
    setVariationId(`${inventoryId}/${variationId}`);
  };

  const renderVariationData = (variations: Variation[]) => {
    return variations.map((variation: Variation, index) => {
      return {
        index: index + 1,
        name: variation.name,
        type: variation.type,
        price: variation.price,
        quantity: variation.quantity ? variation.quantity : 0,
        bin: (
          <div onClick={() => handleDeleteVariant(variation.id)}>
            <BinIcon color="red" />
          </div>
        ),
      };
    });
  };

  const renderRestockData = (restock: RestockHistory[]) => {
    return restock?.map((restock: RestockHistory, index) => {
      return {
        index: index + 1,
        name: details?.name,
        quantityRestocked: restock.quantityRestocked,
        variation: restock.variations,
        date: formattedDate(restock.date),
        oldSellingPrice: restock.oldSellingPrice,
        newSellingPrice: restock.newSellingPrice,
      };
    });
  };

  const modalState = variations && variations.length > 0 ? true : false;

  const infoCardData = [
    {
      title: 'Total Purchase Amount',
      value: `${formatMoney(stats.totalCostPrice)}`,
      color: 'inactive',
      icon: <Naira />,
      isLoading: isLoading,
    },
    {
      title: 'Total Purchase',
      value: `${formatMoney(stats.totalPurchase)}`,
      color: 'inactive',
      icon: <NariaIcon />,
      isLoading: isLoading,
    },
    {
      title: 'Selling Price',
      value: `${formatMoney(stats.totalSellingPrice)}`,
      color: 'inactive',
      icon: <NariaIcon />,
      isLoading: false,
    },
  ] as InfoCardProps[];

  return (
    <>
      {showPrompt && (
        <div className="fixed top-0 right-0 z-40 h-screen p-4 overflow-y-auto transition-transform bg-white w-80 sm:w-[498px]">
          <EditInventory
            fetchDetail={getInventoryDetail}
            onClose={onClose}
            setShowPrompt={setShowPrompt}
            id={inventoryId}
            details={details}
          />
        </div>
      )}
      {showDeleteVariant && (
        <SidebarDrawer
          title={'Delete Variation'}
          show={showDeleteVariant}
          position={'right'}
          onClose={() => setShowDeleteVariant(false)}
        >
          <VariantDeletePrompt
            id={inventoryId}
            fetchDetail={getInventoryDetail}
            variationId={variationId}
            setShowPrompt={setShowDeleteVariant}
          />
        </SidebarDrawer>
      )}
      {isLoading ? (
        <PageLoader />
      ) : (
        <div>
          <div className="flex mb-8 items-center justify-between">
            <a href="/inventories">
              <p className="font-medium text-[20px] flex items-center gap-4">
                <span className="">
                  <ExpandLeft />
                </span>
                Inventory Details
              </p>
            </a>
            <div className="flex items-center space-x-4 md:hidden">
              <div>
                <AddSquareIcon />
              </div>
              <div onClick={() => setShowPrompt(true)}>
                <EditIcon />
              </div>
              <div onClick={() => setShowDeleteInventory(true)}>
                <DeleteIcon />
              </div>
            </div>
            <div className="hidden lg:flex items-center gap-12">
              <div>
                <GroupButton
                  buttons={[
                    {
                      text: 'Edit',
                      icon: <EditIcon />,
                      fontSize: 'text-[17px]',
                      onClick: () => setShowPrompt(true),
                    },
                    {
                      text: 'Delete',
                      icon: <DeleteIcon />,
                      fontSize: 'text-[17px]',
                      onClick: () => setShowDeleteInventory(true),
                    },
                  ]}
                />
              </div>
              <div
                onClick={handleModal}
                className="hidden lg:flex h-[48px] bg-gray-50 border border-[#044E97] text-[#044E97] px-[34px]  items-center cursor-pointer"
              >
                <h2 className="">Restock</h2>
              </div>
            </div>
          </div>
          <div className="">
            <div className="bg-white p-4 md:bg-transparent md:p-0">
              <div className="flex mb-4 items-center justify-between lg:hidden">
                <h2 className="text-xl line font-medium md:font-medium md:text-3xl">{details?.name}</h2>
                <div className="flex gap-6">
                  <div className="hidden md:block lg:hidden">
                    <GroupButton
                      buttons={[
                        {
                          text: 'Edit',
                          icon: <EditIcon />,
                          fontSize: 'text-[17px]',
                          onClick: () => setShowPrompt(true),
                        },
                        {
                          text: 'Delete',
                          icon: <DeleteIcon />,
                          fontSize: 'text-[17px]',
                          onClick: () => setShowDeleteInventory(true),
                        },
                      ]}
                    />
                  </div>
                  <div className="hidden md:flex ring-1 ring-blue-600 h-[48px] px-7 bg-white items-center">
                    <p className="text-[#044E97]" onClick={handleModal}>
                      Restock
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-3 md:flex md:items-center md:justify-between lg:mt-[64px]">
                <div className="hidden lg:flex !mt-0 lg:flex-col lg:items-start">
                  <h3>Name</h3>
                  <p className="font-semibold">{details?.name}</p>
                </div>
                <div className="flex items-center !mt-0 justify-between md:flex-col md:items-start">
                  <h3>Quantity Left</h3>
                  <p className="font-semibold">{details?.quantity}</p>
                </div>

                <div className="flex items-center !mt-0 justify-between md:flex-col md:items-start">
                  <h3>Unit Price</h3>
                  <p className="font-semibold">{details?.unitprice}</p>
                </div>
                <div className="flex items-center mb-4 justify-between md:flex-col md:items-start !mt-0">
                  <h3>Date Added</h3>
                  <p className="font-semibold">{formattedDate(details?.date)}</p>
                </div>
                <div className="flex items-center !mt-0 justify-between md:flex-col md:items-start">
                  <h3>{details?.desc ? 'Description' : ''}</h3>
                  <p className="font-semibold md:!ml-0">
                    {details?.desc ? <TruncatedText text={details?.desc} maxLength={20} /> : ''}
                  </p>
                </div>
              </div>
            </div>
            <div className="lg:hidden space-y-4 mt-6 md:mt-12">
              <div className="grid md:grid-cols-2 gap-4">
                {infoCardData.slice(0, 2).map((cardData, i) => (
                  <InfoCard {...cardData} />
                ))}
              </div>
              <div className="grid gap-4">
                {infoCardData.slice(2, 3).map((cardData, i) => (
                  <InfoCard {...cardData} />
                ))}
              </div>
            </div>
            <div className="hidden lg:block mt-8">
              <div className="grid grid-cols-3 gap-6 lg:gap-[20px]">
                {infoCardData.map((cardData, i) => (
                  <div key={i}>
                    <InfoCard {...cardData} />
                  </div>
                ))}
                {infoCardData.length < 3 &&
                  Array.from({ length: 3 - infoCardData.length }).map((_, index) => <div key={index}></div>)}
              </div>
            </div>
          </div>
          {variations ? (
            <VariationTable variations={variations} data={renderVariationData(variations)} isLoading={isLoading} />
          ) : (
            <div className="mt-6 space-y-4 font-semibold">
              <p className=" text-red-500 "> The inventory has no variations</p>
            </div>
          )}
          {restock?.length > 0 && <PurchaseTable isLoading={isLoading} data={renderRestockData(restock)} />}
        </div>
      )}
      {showDeleteInventory && (
        <div>
          <SidebarDrawer
            title={'Delete Inventory'}
            show={showDeleteInventory}
            position={'right'}
            onClose={() => setShowDeleteInventory(false)}
          >
            <InventoryDeletePrompt setShowPrompt={setShowPrompt} inventoryId={inventoryId} />
          </SidebarDrawer>
        </div>
      )}
      {openModal &&
        createPortal(
          <InventoryModal
            modalSize={modalState}
            children={<Restock restockId={inventoryId} setOpenModal={setOpenModal} />}
            setOpenModal={setOpenModal}
          />,
          document.body,
        )}
    </>
  );
};

export default ViewInventoryPage;
